import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Divider, Grid, Typography, Box, Container, InputBase } from '@material-ui/core';
import {
  Search,
  History as HistoryIcon,
  ChevronRight
} from '@material-ui/icons';

import Carousel from 'react-material-ui-carousel';
import UIClock from '../UI/Clock/UIClock';
import UILogo from '../UI/Logo/UILogo';
import FirstAccessOnboardingModal from '../UI/Modal/FirstAccessOnboardingModal';
import InstallPWA from '../UI/InstallPWA/InstallPWA';
import StoreContext from '../Store/context';

import './homePage.css';
import useStorage from '../../utils/useStorage';
import StickyNotes from './Cards/stickyNotes';
import GoalCard from './Cards/goalCard';
import GoalTags from './Tags/goalTags';
import { formattedDateTime } from '../../utils/utils';

import TrialOnboardingGuide from './TrialOnboardingGuide';
import DuxiaInsightsPanel from './DuxiaInsights/DuxiaInsightsPanel';
import useDuxiaInsights from './DuxiaInsights/useDuxiaInsights';
import SubscriptionCheckoutModal from '../UI/Subscription/SubscriptionCheckoutModal';
import { hasManagerAccessLevel, isManagerOrAbove } from '../../utils/permissionUtils';

const HomePage = () => {
  const { credencials, setChatbotOpen } = useContext(StoreContext);
  const history = useHistory();
  const [recentPages, setRecentPages] = useState([]);
  const [showFirstAccessModal, setShowFirstAccessModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);

  // Compute trial status & days remaining
  const isTrial =
    credencials?.companyData?.is_trial === true ||
    credencials?.companyData?.status === 'trial' ||
    credencials?.companyData?.trial_used === true ||
    credencials?.is_trial === true;
  const endTimeStr = credencials?.companyData?.endTime || credencials?.licenseEndTime;
  let daysRemaining = 14;
  if (endTimeStr) {
    try {
      const endDt = new Date(endTimeStr.replace(' ', 'T'));
      const nowDt = new Date();
      const diffTime = endDt - nowDt;
      daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    } catch (e) {
      daysRemaining = 14;
    }
  }

  const companyId = credencials?.company || credencials?.companyData?.id || 'default';
  const [guideDismissed, setGuideDismissed] = useState(() => {
    return localStorage.getItem(`duzz_onboarding_dismissed_${companyId}`) === 'true';
  });

  useEffect(() => {
    setGuideDismissed(localStorage.getItem(`duzz_onboarding_dismissed_${companyId}`) === 'true');
  }, [companyId]);

  const showOnboardingGuide = isTrial && !guideDismissed;
  const isManager = isManagerOrAbove(credencials) || hasManagerAccessLevel(credencials);
  const duxiaInsights = useDuxiaInsights(companyId);
  const showDuxiaInsights = isManager && duxiaInsights.hasInsights;

  useEffect(() => {
    const pages = JSON.parse(localStorage.getItem('recentPages') || '[]');
    setRecentPages(pages);
  }, []);

  // Check if first access onboarding modal should be shown (must align with showOnboardingGuide)
  useEffect(() => {
    if (!showOnboardingGuide) {
      setShowFirstAccessModal(false);
      return;
    }
    if (credencials && companyId && companyId !== 'default') {
      const alreadySeen = localStorage.getItem(`duzz_first_access_onboarding_${companyId}`) === 'true';
      if (!alreadySeen) {
        setShowFirstAccessModal(true);
      }
    }
  }, [credencials, companyId, showOnboardingGuide]);

  const handleCloseFirstAccessModal = () => {
    setShowFirstAccessModal(false);
    if (companyId && companyId !== 'default') {
      localStorage.setItem(`duzz_first_access_onboarding_${companyId}`, 'true');
    }
  };

  const linkToPage = (route) => {
    if (history.location.pathname !== route) {
      history.push(route);
    }
  };

  function getTimeDay() {
    const currentHours = new Date().getHours();
    if (currentHours >= 0 && currentHours < 12) return 'Bom dia';
    if (currentHours >= 12 && currentHours < 18) return 'Boa tarde';
    return 'Boa noite';
  }

  return (
    <>
      <Grid container className="home-page">
        {/* Hero Section */}
        <Grid item xs={12} md={7} lg={8} className="home-page__info">
          <Container maxWidth="md" style={{ paddingLeft: 16, paddingRight: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={6} width="100%">
              <Box className="home-page__logo-wrapper" flex={1} minWidth={0} mr={2} textAlign="left">
                <UILogo
                  name={credencials.companyData?.pseudonimo?.split(' ')[0]}
                  lastName={credencials.companyData?.pseudonimo
                    ?.split(' ')
                    .slice(1)
                    .join(' ')}
                  size={50}
                />
              </Box>
              <Box
                className="home-page__clock-container"
                p={1}
                bgcolor="rgba(0, 0, 0, 0.05)"
                borderRadius={50}
                display="inline-flex"
                alignItems="center"
                style={{ flexShrink: 0, marginLeft: 16, border: '1px solid rgba(0,0,0,0.05)' }}
              >
                <UIClock />
              </Box>
            </Box>

            <Box textAlign="left" width="100%" mb={3}>
              <Typography variant="h4" style={{ fontWeight: 700, color: '#1e293b' }}>
                {getTimeDay()}, <span>{credencials.name}!</span> 👋
              </Typography>
            </Box>

            {/* Recent Pages - Expanded */}
            {recentPages.length > 0 && (
              <Box className="recent-pages-section" width="100%">
                <Typography variant="button" style={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  letterSpacing: '1.5px',
                  fontWeight: 700,
                  fontSize: '0.75rem'
                }}>
                  CONTINUE DE ONDE PAROU
                </Typography>
                <Box className="recent-pages-grid">
                  {recentPages.slice(0, 8).map((page, index) => (
                    <Box
                      key={index}
                      className="recent-page-card"
                      onClick={() => linkToPage(page.path)}
                    >
                      <Box className="recent-page-icon">
                        <HistoryIcon style={{ fontSize: '1rem', color: '#1e293b' }} />
                      </Box>
                      <Typography variant="body2" noWrap style={{ flex: 1, fontWeight: 500, fontSize: '0.85rem' }}>
                        {page.name}
                      </Typography>
                      <ChevronRight style={{ fontSize: '0.9rem', opacity: 0.4 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Container>
        </Grid>

        {/* Side Content / Onboarding Guide & Carousel */}
        <Grid item xs={12} md={5} lg={4} className="home-page__sliders">
          <Box mb={2}>
            <Typography className="sticky-notes__title">Novidades & Destaques</Typography>
          </Box>

          <Carousel
            navButtonsAlwaysInvisible
            autoPlay={!showOnboardingGuide}
            interval={25000}
            animation="slide"
            indicatorContainerProps={{
              className: 'home-page__carousel-indicators',
            }}
            indicatorIconButtonProps={{
              className: 'home-page__carousel-indicator-btn',
            }}
            activeIndicatorIconButtonProps={{
              className: 'home-page__carousel-indicator-btn--active',
            }}
            className="home-page__sliders-carousel"
          >
            {[
              showOnboardingGuide && (
                <Box key="onboarding-guide" px={0.5} className="home-page__slide-item">
                  <TrialOnboardingGuide onDismiss={() => setGuideDismissed(true)} />
                </Box>
              ),
              showDuxiaInsights && (
                <Box key="duxia-insights-panel" px={0.5} className="home-page__slide-item">
                  <DuxiaInsightsPanel insightsHook={duxiaInsights} />
                </Box>
              ),
              <Box key="sale-formats" px={0.5} className="home-page__slide-item">
                <GoalTags title="ATUALIZAÇÃO" subtitle="Formatos de Venda" iconColor="#0284c7" />
                <Box my={2}>
                  <Divider />
                </Box>
                <GoalCard
                  title="O Cadastro de Produtos Mudou!"
                  subtitle="Agora você pode cadastrar múltiplos formatos de venda para o mesmo produto: venda garrafas em doses, fardos em latas avulsas ou porções a granel com controle e baixa automática no estoque principal."
                  actionText="Conhecer Formatos"
                  iconColor="#0284c7"
                  onClick={() => linkToPage('/home/produto/pesquisar')}
                />
              </Box>,
              <Box key="label-print" px={0.5} className="home-page__slide-item">
                <GoalTags title="NOVIDADE" subtitle="Impressão de Etiquetas" iconColor="#7c3aed" />
                <Box my={2}>
                  <Divider />
                </Box>
                <GoalCard
                  title="Impressão de Etiquetas de Código de Barras"
                  subtitle="Gere e imprima etiquetas personalizadas para seus produtos e categorias com código de barras, QR Code, preço e referência, prontas para gôndolas e prateleiras."
                  actionText="Imprimir Etiquetas"
                  iconColor="#7c3aed"
                  onClick={() => linkToPage('/home/produto/pesquisar')}
                />
              </Box>,
              <Box key="pdv-mode" px={0.5} className="home-page__slide-item">
                <GoalTags title="NOVIDADE" subtitle="Frente de Caixa (PDV)" iconColor="#10b981" />
                <Box my={2}>
                  <Divider />
                </Box>
                <GoalCard
                  title="Novo Modo PDV de Alta Velocidade"
                  subtitle="Ideal para caixas e balcões com fluxo rápido de atendimento: leitura ultra veloz de código de barras, controle 100% por atalhos de teclado (F2 a F10), cálculo dinâmico de troco, modo escuro e tela cheia imersiva sem distrações."
                  actionText="Abrir Modo PDV"
                  iconColor="#10b981"
                  onClick={() => linkToPage('/pdv')}
                />
              </Box>,
              <Box key="store-site" px={0.5} className="home-page__slide-item">
                <GoalTags title="NOVIDADE" subtitle="Vitrine Virtual" iconColor="#3b82f6" />
                <Box my={2}>
                  <Divider />
                </Box>
                <GoalCard
                  title="Site para sua Loja"
                  subtitle="Tenha um site para apresentar seus produtos pros seus clientes. Adicione seu whatsapp para receber pedidos"
                  actionText="Conhecer"
                  iconColor="#3b82f6"
                  onClick={() => linkToPage('/home/gestao/catalogo')}
                />
              </Box>,
              isTrial && (
                <Box key="trial-info" px={0.5} className="home-page__slide-item">
                  <GoalTags
                    title="DEGUSTAÇÃO"
                    subtitle={daysRemaining > 0 ? `${daysRemaining} ${daysRemaining === 1 ? 'dia restante' : 'dias restantes'}` : 'Expira hoje'}
                    iconColor={daysRemaining <= 3 ? '#dc2626' : '#2563eb'}
                  />
                  <Box my={2}>
                    <Divider />
                  </Box>
                  <GoalCard
                    title="Período de Degustação"
                    subtitle={
                      daysRemaining <= 3
                        ? `Faltam apenas ${daysRemaining} ${daysRemaining === 1 ? 'dia' : 'dias'} para encerrar sua degustação. Garanta já o seu plano!`
                        : `Você tem ${daysRemaining} dias de acesso gratuito liberado. Garanta seu plano para continuar gerenciando sem interrupções.`
                    }
                    actionText="Garantir Plano"
                    iconColor={daysRemaining <= 3 ? '#dc2626' : '#17497F'}
                    onClick={() => setShowCheckoutModal(true)}
                  />
                </Box>
              ),
              <Box key="license-info" px={0.5} className="home-page__slide-item">
                <GoalTags title="CONTA" subtitle="Licença & Assinatura" iconColor="#f59e0b" />
                <Box my={2}>
                  <Divider />
                </Box>
                <StickyNotes
                  title="Duração da Licença"
                  content={`${credencials.companyData?.pseudonimo}, sua licença vence em: ${formattedDateTime(credencials.companyData?.endTime)}`}
                  actionText="Gerenciar / Renovar"
                  onClick={() => linkToPage('/home/gestao/empresa')}
                />
              </Box>
            ].filter(Boolean)}
          </Carousel>
        </Grid>
      </Grid>

      {/* Modal de Onboarding para Primeiro Acesso */}
      <FirstAccessOnboardingModal
        open={showFirstAccessModal}
        onClose={handleCloseFirstAccessModal}
      />

      <SubscriptionCheckoutModal
        open={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
      />

      <InstallPWA />
    </>
  );
};

export default HomePage;